import { useState } from 'react';
import Head from 'next/head';
import App, { AppProps, AppContext, AppInitialProps } from 'next/app';
import { ApolloProvider } from '@apollo/client';
import setupLocatorUI from '@locator/runtime';
import { Provider as JotaiProvider, createStore } from 'jotai';

import GlobalSocialPlugin from '@/components/GlobalSocialPlugin';
import { generateApolloClient } from '@/apollo/client';
import { getApolloHeader } from '@/utils/headers';
import GlobalLayout from '@/layouts/GlobalLayout/GlobalLayout';
import { DEVICE_NAME_HEADER } from '@pkgtour-workspace/common/dist/constants/header';
import Initializer from '@/components/Common/Initializer';
import NlogPageViewProvider from '@/components/NlogPageViewProvider';

import './app.scss';
import '@travel-ui/pc-header/pc-header.css';
import '@travel-ui/pc-footer/pc-footer.css';
import '@travel-ui/mobile-footer/mo-footer.css';
import '@techminds/ui/dist/index.css';
import { DEFAULT_IMAGE } from './_document.page';

const DEFAULT_TITLE = '네이버 티켓·패키지';
const DEFAULT_DESCRIPTION = '패키지여행, 에어텔, 티켓패스 등 네이버에서 탐색, 비교부터 구매까지 쉽고 빠르게!';
const DEFAULT_ICON = `https://techminds.pstatic.net/pkgtour/assets/meta/pkgtour.ico`;
const DEFAULT_APPLE_TOUCH_ICON = `https://techminds.pstatic.net/pkgtour/assets/meta/AppleTouch_icon.png`;

interface AppOwnProps {
  apolloHeaders: {
    cookie?: string;
    deviceType?: string;
    nClientIp?: string;
  };
  device: 'pc' | 'mo';
}

if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
  setupLocatorUI();
}

const appStore = createStore();

export default function MyApp({ Component, pageProps, apolloHeaders, device }: AppProps & AppOwnProps) {
  const [apolloClient] = useState(
    generateApolloClient({
      ...apolloHeaders,
      initialState: pageProps?.initialApolloState,
    }),
  );

  const metaDescription = pageProps?.meta?.description ?? DEFAULT_DESCRIPTION;
  const icon = pageProps?.meta?.iconImage || DEFAULT_ICON;

  return (
    <>
      <Head>
        <title>{pageProps?.meta?.title || DEFAULT_TITLE}</title>
        <meta
          key="viewport"
          name="viewport"
          content="initial-scale=1.0,user-scalable=no,maximum-scale=1,width=device-width"
        />
        <meta key="description" name="description" content={metaDescription} />
        <meta key="og:title" property="og:title" content={pageProps?.meta?.title || DEFAULT_TITLE} />
        <meta key="og:image" property="og:image" content={pageProps?.meta?.iconImage || DEFAULT_IMAGE} />
        <meta key="og:description" property="og:description" content={metaDescription} />
        <meta key="twitter:description" property="twitter:description" content={metaDescription} />
        <link rel="icon" href={icon} />
        <link rel="shortcut icon" href={icon} />
        <link rel="apple-touch-icon" href={DEFAULT_APPLE_TOUCH_ICON} sizes="180x180" />
      </Head>
      <JotaiProvider store={appStore}>
        <ApolloProvider client={apolloClient}>
          <Initializer device={device}>
            <div className="package-tour">
              <GlobalLayout myHeaderTitle={pageProps?.layout?.myHeaderTitle || ''}>
                <NlogPageViewProvider>
                  <Component {...pageProps} />
                  <GlobalSocialPlugin />
                </NlogPageViewProvider>
              </GlobalLayout>
            </div>
          </Initializer>
        </ApolloProvider>
      </JotaiProvider>
    </>
  );
}

MyApp.getInitialProps = async (context: AppContext): Promise<AppInitialProps & AppOwnProps> => {
  const ctx = await App.getInitialProps(context);

  const apolloHeaders = getApolloHeader(context.ctx.req);
  const device = context.ctx.req?.headers?.[DEVICE_NAME_HEADER] as 'pc' | 'mo';

  const initialProps = {
    ...ctx,
    apolloHeaders,
    device,
  };

  return initialProps;
};
